import { useMemo } from 'react';
import { SERVICE_QUESTIONS } from './coverQuizData';
import type { CoverQuizAnswers, QuizOption } from './coverQuizData';
import type { QuizLang, Txt } from './quizLang';
import { readClientNotes, updateClientNotes, useClientNotes } from '../Advisor/ClientV2/clientNotesStore';
import type { ClientNotes } from '../Advisor/ClientV2/clientNotesStore';
import type { ServicePrefs } from '../../store/clientNotes/api';

/**
 * 你希望的服務方式 — the client's quick feedback on how they like to be served,
 * kept on the client's record once the adviser imports it, so it is still there
 * at the next follow-up (the drawer and 重點跟進), not only inside the quiz.
 *
 * Stored as option keys under `factsheet.smartIdea.servicePrefs` through the
 * adviser-notes endpoint; the labels live here, with the questions.
 */
export const SERVICE_PREF_QUESTION = { contact: 'S1', info: 'S4', priority: 'S5' } as const;
export type ServicePrefField = keyof typeof SERVICE_PREF_QUESTION;
const FIELDS = Object.keys(SERVICE_PREF_QUESTION) as ServicePrefField[];

export type ServicePrefLine = { field: ServicePrefField; short: Txt; option: QuizOption };

const optionOf = (field: ServicePrefField, key?: string | null): QuizOption | undefined => {
  const question = SERVICE_QUESTIONS.find(q => q.id === SERVICE_PREF_QUESTION[field]);
  return key && question ? (question.options || []).find(o => o.key === key) : undefined;
};

/** What the client answered, as preferences. Only answered questions appear. */
export const servicePrefsOfAnswers = (answers: CoverQuizAnswers): ServicePrefs => {
  const picks = answers.picks || {};
  const prefs: ServicePrefs = {};
  FIELDS.forEach(field => {
    const key = picks[SERVICE_PREF_QUESTION[field]];
    if (optionOf(field, key)) prefs[field] = key;
  });
  return prefs;
};

/** The preferences in question order, with their labels; an unknown key is skipped. */
export const servicePrefLines = (prefs?: ServicePrefs | null): ServicePrefLine[] =>
  FIELDS.map(field => {
    const question = SERVICE_QUESTIONS.find(q => q.id === SERVICE_PREF_QUESTION[field]);
    const option = optionOf(field, prefs?.[field]);
    return question && option ? { field, short: question.short, option } : null;
  }).filter((line): line is ServicePrefLine => Boolean(line));

/** One line for a card: 「💬 WhatsApp 訊息 · 🤝 不催促、不推銷」, in `lang`. Empty when nothing is on file. */
export const servicePrefsSummary = (prefs: ServicePrefs | null | undefined, lang: QuizLang): string =>
  servicePrefLines(prefs)
    .map(line => `${line.option.icon} ${line.option.label[lang]}`)
    .join(' · ');

export const hasServicePrefs = (prefs?: ServicePrefs | null): boolean => servicePrefLines(prefs).length > 0;

export const sameServicePrefs = (a?: ServicePrefs | null, b?: ServicePrefs | null): boolean =>
  FIELDS.every(field => (a?.[field] || null) === (b?.[field] || null));

const prefsOf = (notes: ClientNotes | null): ServicePrefs | null => (notes && notes.smartIdea.servicePrefs) || null;

export const readServicePrefs = (clientId?: string | null): ServicePrefs | null => prefsOf(readClientNotes(clientId));

export const useServicePrefs = (clientId?: string | null): ServicePrefs | null => {
  const notes = useClientNotes(clientId);
  return useMemo(() => prefsOf(notes), [notes]);
};

/** Replaces what is on file: a field the quiz did not answer is cleared, so the record matches the quiz. */
export const writeServicePrefs = (clientId: string, prefs: ServicePrefs): void => {
  if (!clientId) return;
  const next: ServicePrefs = {};
  FIELDS.forEach(field => {
    next[field] = prefs[field] || null;
  });
  const kept: ServicePrefs = {};
  FIELDS.forEach(field => {
    if (next[field]) kept[field] = next[field];
  });
  void updateClientNotes(clientId, { smartIdea: { servicePrefs: next } }, notes => ({
    ...notes,
    smartIdea: { ...notes.smartIdea, servicePrefs: kept }
  }));
};
